/**
 * How the publication activity feed reads an audit row's `data`.
 *
 * `data` is schemaless on the wire and each verb shapes it its own way, so the
 * feed never trusts it: every event goes through `defaultView`, which renders
 * any well-formed `changes` list as a diff and everything else as plain
 * details. A verb whose payload carries a change in another shape gets a spec
 * in `ACTIVITY_SPECS`; a spec that does not recognise its payload falls back
 * to the default, so a verb unknown here — or a known one whose schema moved —
 * still renders.
 */
import { formatDate, formatDateTime } from '@/utils/format';
import { diffFieldLabel } from './diffFieldLabel';

export interface FieldChange {
  path: string;
  old: unknown;
  new: unknown;
}

export interface ActivityView {
  changes: FieldChange[];
  /** Payload keys not shown as changes, in payload order. */
  details: [string, unknown][];
}

export type ActivityGroup = 'data' | 'moderation';

type Payload = Record<string, unknown>;

interface ActivitySpec {
  group: ActivityGroup;
  /** Payload keys that only identify things the row already shows. */
  hidden?: string[];
  /** Null when the payload is not the shape the spec expects. */
  view?: (data: Payload) => ActivityView | null;
}

const isRecord = (value: unknown): value is Payload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFieldChange = (value: unknown): value is FieldChange =>
  isRecord(value) &&
  typeof value.path === 'string' &&
  'old' in value &&
  'new' in value;

export const defaultView = (data: Payload): ActivityView => {
  const { changes, ...rest } = data;
  if (Array.isArray(changes) && changes.every(isFieldChange)) {
    return { changes, details: Object.entries(rest) };
  }
  return { changes: [], details: Object.entries(data) };
};

/** Early access carries one product's `accessible_from` as a before/after pair. */
const earlyAccessView =
  (cancelled: boolean) =>
  (data: Payload): ActivityView | null => {
    if (typeof data.kind !== 'string') return null;
    const { kind, accessible_from, previous_accessible_from, ...rest } = data;
    return {
      changes: [
        {
          path: `${kind}.accessible_from`,
          old: previous_accessible_from ?? null,
          new: cancelled ? null : (accessible_from ?? null),
        },
      ],
      details: Object.entries(rest),
    };
  };

export const ACTIVITY_SPECS: Record<string, ActivitySpec> = {
  'publication.updated': { group: 'data', hidden: ['products'] },
  'publication.import_applied': { group: 'data' },
  'publication.early_access_scheduled': {
    group: 'data',
    hidden: ['product'],
    view: earlyAccessView(false),
  },
  'publication.early_access_cancelled': {
    group: 'data',
    hidden: ['product'],
    view: earlyAccessView(true),
  },
  'publication.mod_status_changed': {
    group: 'moderation',
    hidden: ['products'],
  },
};

/** The verbs a group chip filters on; a verb with no spec is in none. */
export const activityGroupVerbs = (group: ActivityGroup): string[] =>
  Object.entries(ACTIVITY_SPECS)
    .filter(([, spec]) => spec.group === group)
    .map(([verb]) => verb);

export const activityView = (
  verb: string | null,
  data: unknown
): ActivityView => {
  if (!isRecord(data)) return { changes: [], details: [] };
  const spec = verb ? ACTIVITY_SPECS[verb] : undefined;
  const view = spec?.view?.(data) ?? defaultView(data);
  const hidden = spec?.hidden ?? [];
  return {
    ...view,
    details: view.details.filter(([key]) => !hidden.includes(key)),
  };
};

// ─── Labels ───────────────────────────────────────────────────────────────────

type Translate = (key: string, fallback?: string) => string;

const T = 'user_catalog.publications.activity';

export const activityFieldLabel = (t: Translate, path: string): string =>
  t(`${T}.fields.${path}`, diffFieldLabel(t, path));

export const activityVerbLabel = (t: Translate, verb: string | null) =>
  verb ? t(`${T}.verbs.${verb}`, verb) : '—';

/** `m.pawlak@…` → `MP`; a single-word local part gives its first two letters. */
export const actorInitials = (email: string): string => {
  const words = (email.split('@')[0] ?? '').split(/[^\p{L}]+/u).filter(Boolean);
  const letters =
    words.length > 1
      ? words.slice(0, 2).map((w) => w[0])
      : [...(words[0] ?? '')].slice(0, 2);
  return letters.join('').toUpperCase() || '?';
};

export const isEmptyValue = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

/** Any payload value as one line of text — unknown shapes fall to JSON. */
export const formatActivityValue = (t: Translate, value: unknown): string => {
  if (isEmptyValue(value)) return t(`${T}.empty_value`);
  if (typeof value === 'boolean') return t(value ? 'common.yes' : 'common.no');
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value)!;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value)!;
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => formatActivityValue(t, v)).join(', ');
  }
  if (isRecord(value) && 'lower' in value && 'upper' in value) {
    const side = (v: unknown) => (v ? formatActivityValue(t, v) : '…');
    return `${side(value.lower)} – ${side(value.upper)}`;
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// ─── Diffs ────────────────────────────────────────────────────────────────────

export type DiffPart = { kind: 'same' | 'added' | 'removed'; text: string };

/** Word-level diff, whitespace kept so the parts join back into the text. */
export const wordDiff = (before: string, after: string): DiffPart[] => {
  const a = before.split(/(\s+)/);
  const b = after.split(/(\s+)/);
  // ponytail: O(n·m) LCS table — fine for descriptions of a few hundred
  // words; switch to Myers if book-length fields ever land here.
  const lcs = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] =
        a[i] === b[j]
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const parts: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => {
    if (!text) return;
    const last = parts[parts.length - 1];
    if (last?.kind === kind) last.text += text;
    else parts.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i++]!);
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      push('removed', a[i++]!);
    } else {
      push('added', b[j++]!);
    }
  }
  while (i < a.length) push('removed', a[i++]!);
  while (j < b.length) push('added', b[j++]!);
  return parts;
};

/** Long enough that a side-by-side cell would bury the edit. */
export const isLongText = (change: FieldChange): boolean =>
  [change.old, change.new].every((v) => v == null || typeof v === 'string') &&
  [change.old, change.new].some((v) => typeof v === 'string' && v.length > 80);
